import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { CommonSelect, type Option, type SelectProps } from 'components/common';
import { useInfiniteCreativeTexts } from '../api';
import type { CreativeText } from '../types';
import { useInView } from 'react-intersection-observer';
import { useFormContext } from 'react-hook-form';
import type { SelectChangeEvent } from '@mui/material';
import type { ListResponse } from 'types/shared';
import { trackCabinetQueryError } from 'lib/datadog';

interface SelectCreativeTextsAttrs {
  open: boolean;
  multiples: boolean;
  placeholder: string;
  asAutocomplete?: boolean;
  name: 'defaultCreativeTextId' | 'additionalCreativeTextsIds';
}

type SelectCreativeTextsProps = Partial<
  Pick<SelectProps, 'handleChange' | 'inputLabel' | 'onClose' | 'required'> &
  SelectCreativeTextsAttrs
>;

const SelectCreativeTexts: FC<SelectCreativeTextsProps> =
  ({
     handleChange,
     name,
     inputLabel,
     multiples,
     placeholder,
     onClose,
     open,
     asAutocomplete,
     required,
   }) => {
    const { ref, inView } = useInView({
      threshold: 0,
    });
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, error } =
      useInfiniteCreativeTexts({
        params: {limit: 1000}
      });
    const { watch, setValue } = useFormContext();

    const defaultCreativeTextId = watch('defaultCreativeTextId') as number;
    const additionalCreativeTextsIds = watch(
      'additionalCreativeTextsIds',
    ) as number[];

    useEffect(() => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    useEffect(() => {
      if (!isError || !error) {
        return;
      }

      trackCabinetQueryError(error, 'facebook', 'creative_text');
    }, [isError, error]);

    const options = useMemo(() => {
      if (!data) return [];

      return data.pages.flatMap((page: ListResponse<CreativeText>) =>
        page.data
          .filter((creativeText: CreativeText) => {
            const id = Number(creativeText.id);
            if (name === 'defaultCreativeTextId') {
              return !additionalCreativeTextsIds?.includes(id);
            }
            if (name === 'additionalCreativeTextsIds') {
              return id !== defaultCreativeTextId;
            }
            return true;
          })
          .map(
            ({ body, id }: CreativeText): Option => ({
              label: body,
              value: Number(id),
            }),
          ),
      );
    }, [data, name, defaultCreativeTextId, additionalCreativeTextsIds]);

    const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value;

      if (
        name === 'defaultCreativeTextId' &&
        typeof value === 'number' &&
        additionalCreativeTextsIds?.includes(value)
      ) {
        setValue(
          'additionalCreativeTextsIds',
          additionalCreativeTextsIds.filter((id) => id !== value),
        );
      }
      if (
        name === 'additionalCreativeTextsIds' &&
        Array.isArray(value) &&
        value.includes(defaultCreativeTextId)
      ) {
        setValue('defaultCreativeTextId', 0);
      }
      handleChange?.(event);
    };

    return (
      <CommonSelect
        name={name ?? 'creativeText'}
        inputLabel={inputLabel ?? 'Creative text'}
        options={options}
        handleChange={handleSelectChange}
        placeholder={placeholder}
        multiples={multiples}
        onClose={onClose}
        open={open}
        loading={isFetchingNextPage}
        loadMoreRef={ref}
        asAutocomplete={asAutocomplete}
        required={required}
      />
    );
  };

export default SelectCreativeTexts;
